import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Box, CircularProgress, Divider, Grid, LinearProgress, Typography } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { RootState } from 'store';
import { isOperatorOrHigher, isSuperAdmin } from 'features/auth/helpers';
import { IPassenger, IReceipt } from '../../types';
import { getReceiptsForOrder } from '../../services';
import { useSaleOrderInvoicingOptional } from 'features/invoices/components/SaleOrderInvoices/InvoicingContext';
import { ClientPaymentCard } from './ClientPaymentCard';

// ─── Helpers URL ──────────────────────────────────────────────────────────────

const generateClientPaymentsUrl = (saleOrderId: string, paymentId?: string): string => {
	const base = `/salesorder/${saleOrderId}?section=cobros-facturas`;
	return paymentId ? `${base}&paymentId=${paymentId}` : base;
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
	saleOrderId: string;
	saleAmount: number;
	passengers: IPassenger[];
	canWrite: boolean;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export const ClientPaymentsSection = ({ saleOrderId, saleAmount, passengers, canWrite }: Props): JSX.Element => {
	const auth = useSelector((state: RootState) => state.auth);
	const canReconcile = isOperatorOrHigher(auth.user);
	const canUnreconcile = isSuperAdmin(auth.user);

	/**
	 * Dentro de la pantalla de la orden los recibos ya vienen prefetcheados por
	 * el SaleOrderInvoicingProvider (se piden al cargar la orden, no al entrar
	 * a esta sección). Fuera del provider (listado global) se fetchea acá.
	 */
	const invoicing = useSaleOrderInvoicingOptional();

	const [payments, setPayments] = useState<IReceipt[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const location = useLocation();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const paymentIdFromURL = new URLSearchParams(location.search).get('paymentId');

	const fetchPayments = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await getReceiptsForOrder(saleOrderId);
			setPayments(res.data);
		} catch {
			setError('No se pudieron cargar los cobros.');
		} finally {
			setLoading(false);
		}
	}, [saleOrderId]);

	const hasProvider = Boolean(invoicing);

	useEffect(() => {
		if (hasProvider) return;
		fetchPayments();
	}, [fetchPayments, hasProvider]);

	useEffect(() => {
		if (!invoicing) return;
		setPayments(invoicing.orderReceipts);
		setLoading(!invoicing.orderReceiptsLoaded);
		setError(invoicing.orderReceiptsError);
	}, [invoicing]);

	// Actualizar la URL cuando se entra a esta sección
	useEffect(() => {
		window.history.replaceState(null, '', generateClientPaymentsUrl(saleOrderId));
	}, [saleOrderId]);

	const handlePaymentUpdated = (updated: IReceipt) => {
		setPayments((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
	};

	// ─── Cálculo resumen ─────────────────────────────────────────────────────

	const totalCobrado = payments.reduce((sum, p) => sum + p.amountInMainCurrency, 0);
	const saldo = saleAmount - totalCobrado;
	const porcentajeCobrado = saleAmount > 0 ? Math.min((totalCobrado / saleAmount) * 100, 100) : 0;

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" py={6}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ m: 2 }}>
				{error}
			</Alert>
		);
	}

	return (
		<Box sx={{ mt: 2 }}>
			{/* ─── Título + botón nuevo ─── */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<PaymentsIcon color="primary" />
					<Typography variant="h6" fontWeight="bold">
						Recibos
					</Typography>
				</Box>
			</Box>

			{/* ─── Resumen financiero ─── */}
			<Box
				sx={{
					p: 2,
					mb: 3,
					borderRadius: 2,
					backgroundColor: 'grey.50',
					border: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={4}>
						<Typography variant="caption" color="text.secondary">
							Total orden
						</Typography>
						<Typography variant="subtitle1" fontWeight="bold">
							USD {saleAmount.toFixed(2)}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="caption" color="text.secondary">
							Total cobrado
						</Typography>
						<Typography
							variant="subtitle1"
							fontWeight="bold"
							color={totalCobrado >= saleAmount ? 'success.main' : 'text.primary'}
						>
							USD {totalCobrado.toFixed(2)}
						</Typography>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="caption" color="text.secondary">
							Saldo pendiente
						</Typography>
						<Typography variant="subtitle1" fontWeight="bold" color={saldo <= 0 ? 'success.main' : 'error.main'}>
							USD {Math.abs(saldo).toFixed(2)}
							{saldo < 0 && ' (exceso)'}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<LinearProgress
								variant="determinate"
								value={porcentajeCobrado}
								color={porcentajeCobrado >= 100 ? 'success' : 'primary'}
								sx={{ flex: 1, height: 8, borderRadius: 4 }}
							/>
							<Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
								{porcentajeCobrado.toFixed(1)}%
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</Box>

			<Divider sx={{ mb: 2 }} />

			{/* ─── Cards de cobros ─── */}
			{payments.length === 0 ? (
				<Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
					<PaymentsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
					<Typography variant="body2">No hay cobros registrados para esta orden.</Typography>
					{canWrite && (
						<Typography variant="caption">
							{'Usá el botón "+" de arriba o el panel lateral para registrar un cobro.'}
						</Typography>
					)}
				</Box>
			) : (
				<Grid container spacing={1.5}>
					{payments.map((payment) => (
						<Grid item xs={12} key={payment.id}>
							<ClientPaymentCard
								payment={payment}
								passengers={passengers}
								canReconcile={canWrite && canReconcile}
								canUnreconcile={canWrite && canUnreconcile}
								onUpdated={handlePaymentUpdated}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</Box>
	);
};
